import { useState } from 'react';
import { useAppSettings } from '../hooks/useAppSettings';
import { useAuthHeaders } from './useAuthHeaders';

interface DeleteFileParams {
    category: string;
    fileName: string;
}

export const useDeleteFile = () => {
    const { settings: appSettings } = useAppSettings();
    const { getAuthHeaders } = useAuthHeaders();
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteSuccess, setDeleteSuccess] = useState(false);

    const deleteFile = async ({ category, fileName }: DeleteFileParams): Promise<void> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsDeleting(true);
        setDeleteError(null);
        setDeleteSuccess(false);

        try {
            const params = new URLSearchParams({
                category,
                fileName,
            });

            const response = await fetch(`${appSettings.api}/DeleteFile?${params.toString()}`, {
                method: 'DELETE',
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Delete file failed (${response.status}): ${errorText}`);
            }

            setDeleteSuccess(true);
        } catch (error: any) {
            setDeleteError(error.message || 'Failed to delete file');
            throw error;
        } finally {
            setIsDeleting(false);
        }
    };

    const clearMessages = () => {
        setDeleteError(null);
        setDeleteSuccess(false);
    };

    return {
        deleteFile,
        isDeleting,
        deleteError,
        deleteSuccess,
        clearMessages,
    };
};
